import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText,
  Paper, Stack, Typography, alpha, useTheme,
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import {
  OverviewIcon, DriverDiscoveryIcon, ModelsIcon, InferencesIcon,
  DeploymentsIcon, AlertsIcon, ActivityIcon, SettingsIcon,
} from '../global/CustomIcons';
import { navPulse } from './GoalTourGuide';

const ScenariosForkIcon = (props) => (
  <AltRouteIcon
    {...props}
    sx={{ ...(props.sx || {}), transform: 'rotate(90deg)' }}
  />
);

export const GOAL_SECTIONS = [
  { seg: '', key: 'overview', label: 'Overview', Icon: OverviewIcon },
  { seg: 'drivers', key: 'drivers', label: 'Driver Discovery', Icon: DriverDiscoveryIcon },
  { seg: 'models', key: 'models', label: 'Models', Icon: ModelsIcon },
  { seg: 'forecast', key: 'forecast', label: 'Forecast', Icon: InferencesIcon },
  { seg: 'scenarios', key: 'scenarios', label: 'Scenarios', Icon: ScenariosForkIcon },
  { seg: 'serving', key: 'serving', label: 'Serving', Icon: DeploymentsIcon },
  { seg: 'alerts', key: 'alerts', label: 'Alerts', Icon: AlertsIcon },
  { seg: 'activity', key: 'activity', label: 'Activity', Icon: ActivityIcon },
  { seg: 'settings', key: 'settings', label: 'Settings', Icon: SettingsIcon },
];

export const SECTION_LABELS = GOAL_SECTIONS.reduce((acc, s) => {
  acc[s.key] = s.label;
  return acc;
}, {});

export const GoalSideNav = ({ goalId, active, highlight }) => (
  <Box
    sx={{
      width: { xs: '100%', md: 180 },
      flexShrink: 0,
      position: { md: 'sticky' },
      top: { md: 16 },
      alignSelf: 'flex-start',
      overflowX: { xs: 'auto', md: 'visible' },
      pb: { xs: 0.5, md: 0 },
    }}
  >
    <List
      disablePadding
      sx={{
        display: { xs: 'flex', md: 'block' },
        gap: { xs: 0.5, md: 0 },
        minWidth: { xs: 'max-content', md: 0 },
      }}
    >
      {GOAL_SECTIONS.map(({ seg, key, label, Icon }) => {
        const selected = active === key;
        const toured = highlight === key && !selected;
        return (
          <ListItemButton
            key={key}
            component={RouterLink}
            to={seg ? `/goals/${goalId}/${seg}` : `/goals/${goalId}`}
            selected={selected}
            disableRipple
            sx={{
              position: 'relative',
              px: 1,
              py: '3px',
              flexGrow: { xs: 0, md: 1 },
              whiteSpace: 'nowrap',
              borderRadius: { xs: '999px', md: 0 },
              ...(toured ? {
                borderRadius: '8px',
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.07),
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '8px',
                  color: 'primary.main',
                  animation: `${navPulse} 1.8s ease-out infinite`,
                  pointerEvents: 'none',
                },
              } : {}),
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '60%',
                width: '4px',
                backgroundColor: 'transparent',
                transition: 'background-color 120ms ease',
                display: { xs: 'none', md: 'block' },
              },
              '&:hover': { backgroundColor: 'transparent' },
              '&:hover::after': { backgroundColor: (t) => alpha(t.palette.primary.main, 0.35) },
              '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'primary.light' },
              '&.Mui-selected::after': { backgroundColor: 'primary.main' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34, color: selected ? 'primary.main' : 'text.secondary' }}>
              <Icon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: 14,
                noWrap: true,
                fontWeight: selected ? 600 : 300,
                letterSpacing: '0.01em',
                color: selected ? 'primary.main' : 'text.primary',
              }}
            />
          </ListItemButton>
        );
      })}
    </List>
  </Box>
);

export const GoalSummaryStrip = ({ phases }) => {
  const theme = useTheme();
  const list = useMemo(() => (Array.isArray(phases) ? phases : []), [phases]);

  const next = useMemo(() => {
    if (!list.length) return null;
    const working = list.find((p) => p.state === 'active' && p.id !== 'live');
    if (working) {
      return { tone: 'info', busy: true, label: working.title, sub: working.summary };
    }
    const attention = list.find(
      (p) => ['failed', 'review', 'blocked'].includes(p.state) && p.cta,
    );
    if (attention) {
      return {
        tone: attention.state === 'failed' ? 'error' : 'warning',
        label: 'Next step',
        sub: attention.cta.label,
      };
    }
    const deployPhase = list.find((p) => p.id === 'deploy');
    if (deployPhase?.state === 'done') {
      return {
        tone: 'success',
        label: 'Live',
        sub: deployPhase.summary || 'Serving and monitoring on fresh data',
      };
    }
    const pending = list.find((p) => p.cta);
    if (pending) return { tone: 'info', label: 'Next step', sub: pending.cta.label };
    return { tone: 'info', label: list[0]?.title, sub: list[0]?.summary };
  }, [list]);

  if (!next) return null;

  const toneColor = theme.palette[next.tone]?.main || theme.palette.info.main;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2, p: 1.5, borderRadius: 2,
        borderLeft: `3px solid ${toneColor}`,
        backgroundColor: alpha(toneColor, 0.04),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: alpha(toneColor, 0.12),
        }}>
          {next.busy
            ? <CircularProgress size={15} sx={{ color: toneColor }} />
            : <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: toneColor }} />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: toneColor, fontWeight: 700, lineHeight: 1.2 }}>
            {next.label}
          </Typography>
          {next.sub && (
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.25 }} noWrap>
              {next.sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};
